import React, { useState } from 'react';
import { X, Search, Loader2, AlertCircle, Hash, CheckCircle } from 'lucide-react';
import { ConsultaPlacaBasicaResponse, ConsultationProductType } from '../types';
import { consultaChassiBasica } from '../services/consultationService';

interface ConsultaChassiBasicaModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: ConsultationProductType;
  userCredits: number;
  onSuccess: (result: ConsultaPlacaBasicaResponse) => void;
  onInsufficientCredits: () => void;
}

const ConsultaChassiBasicaModal: React.FC<ConsultaChassiBasicaModalProps> = ({
  isOpen,
  onClose,
  product,
  userCredits,
  onSuccess,
  onInsufficientCredits,
}) => {
  const [chassi, setChassi] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const formatChassi = (value: string): string => {
    // Remove non-alphanumeric characters and convert to uppercase
    return value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 17);
  };

  const handleChassiChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatChassi(e.target.value);
    setChassi(formatted);
    setError('');
  };

  const validateChassi = (chassi: string): boolean => {
    // Chassi must have 8-17 alphanumeric characters
    return chassi.length >= 8 && chassi.length <= 17;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Check credits
    if (userCredits < product.creditCost) {
      onInsufficientCredits();
      return;
    }

    // Validate chassi
    if (!validateChassi(chassi)) {
      setError('Chassi inválido. Deve conter de 8 a 17 caracteres alfanuméricos.');
      return;
    }

    setLoading(true);

    try {
      const result = await consultaChassiBasica(chassi);
      onSuccess(result);
      setChassi('');
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Erro ao consultar chassi. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setChassi('');
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg bg-gradient-to-b from-slate-900 to-slate-950 rounded-3xl border border-slate-700/50 shadow-2xl overflow-hidden">
        {/* Gradient top border */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-cyan-500 to-teal-500" />
        
        {/* Header */}
        <div className="p-6 border-b border-slate-800/50">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-2xl border border-blue-500/30">
                <Hash className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{product.name}</h2>
                <p className="text-sm text-slate-400 mt-1">
                  {product.description}
                </p>
              </div>
            </div>
            <button 
              onClick={handleClose}
              disabled={loading}
              className="p-2 hover:bg-slate-800 rounded-xl transition-colors text-slate-400 hover:text-white disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Chassi Input */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Hash className="w-4 h-4 text-blue-400" />
              Número do Chassi
            </label>
            <div className="relative">
              <input
                type="text"
                value={chassi}
                onChange={handleChassiChange}
                placeholder="9BWZZZ377VT004251"
                className="w-full bg-slate-800/50 border-2 border-slate-700/50 rounded-2xl py-4 px-6 text-white text-lg font-mono tracking-wider uppercase text-center focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all placeholder:text-slate-600 placeholder:tracking-wider"
                disabled={loading}
                autoFocus
              />
              {/* Character counter */}
              <span className={`absolute right-4 top-1/2 -translate-y-1/2 text-xs ${
                chassi.length >= 8 && chassi.length <= 17 
                  ? 'text-emerald-400' 
                  : chassi.length > 0 
                    ? 'text-amber-400' 
                    : 'text-slate-500'
              }`}>
                {chassi.length}/17
              </span>
            </div>
            <p className="text-xs text-slate-500 text-center">
              Chassi deve conter de 8 a 17 caracteres alfanuméricos
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-3 text-red-400 text-sm bg-red-500/10 p-4 rounded-xl border border-red-500/20">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Features */}
          <div className="p-4 bg-slate-800/30 rounded-2xl border border-slate-700/30">
            <p className="text-xs font-semibold text-slate-400 mb-3 uppercase tracking-wider">
              Esta consulta inclui:
            </p>
            <div className="grid grid-cols-2 gap-2">
              {product.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs text-slate-300">
                  <CheckCircle className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                  {feature}
                </div>
              ))}
            </div>
          </div>

          {/* Cost Info */}
          <div className="flex items-center justify-between p-4 bg-blue-500/10 rounded-2xl border border-blue-500/20">
            <div>
              <p className="text-sm text-blue-300">Custo da consulta</p>
              <p className="text-2xl font-bold text-blue-400">
                {product.creditCost} crédito{product.creditCost > 1 ? 's' : ''}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-400">Seu saldo</p>
              <p className="text-xl font-bold text-white">{userCredits} créditos</p>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || chassi.length < 8}
            className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 disabled:from-slate-700 disabled:to-slate-700 disabled:cursor-not-allowed text-white font-bold py-4 rounded-2xl transition-all flex items-center justify-center gap-3 text-lg shadow-lg shadow-blue-500/20 disabled:shadow-none"
          >
            {loading ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                Consultando...
              </>
            ) : (
              <>
                <Search className="w-6 h-6" />
                Consultar Chassi
              </>
            )}
          </button>

          {userCredits < product.creditCost && (
            <p className="text-center text-sm text-red-400">
              Você não tem créditos suficientes para esta consulta.
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default ConsultaChassiBasicaModal;

